import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Filter } from "lucide-react";
import {
  DeleteUserProfiel,
  UpdateUserRole,
} from "../../../../redux/actions/Admin/AdminUser";
import { ApiMediaURL } from "../../../../setting/GlobleVariable";
import { Loading } from "notiflix";
import { GetUserOrderById } from "../../../../redux/actions/Admin/AdminOrders";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const AVATAR_COLORS = [
  "#E52324",
  "#3B82F6",
  "#8B5CF6",
  "#10B981",
  "#F59E0B",
  "#06B6D4",
  "#EC4899",
  "#6366F1",
];

const ROLE_FILTERS = ["All", "admin", "user"];

function getRoleBadgeClass(role) {
  const r = (role || "").toLowerCase();
  if (r === "admin") return "bg-red-500/15 text-red-400";
  return "bg-emerald-500/15 text-emerald-400";
}

function formatRoleLabel(role) {
  if (!role) return "—";
  return role.charAt(0).toUpperCase() + role.slice(1);
}

function getAvatarColor(name, index) {
  if (!name) return AVATAR_COLORS[index % AVATAR_COLORS.length];
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getDisplayName(item) {
  const first = item?.firstName || "";
  const last = item?.lastName || "";
  const full = `${first} ${last}`.trim();
  return full || item?.email || "User";
}

const CustomerTable = ({ setActive }) => {
  const dispatch = useDispatch();
  const AdminUsers = useSelector((state) => state.user.AdminAllUser);
  const adminloading = useSelector((state) => state.user.adminloading);

  const [roleFilter, setRoleFilter] = useState("All");
  const [openMenuIndex, setOpenMenuIndex] = useState(null);

  useEffect(() => {
    if (adminloading === true) {
      Loading.standard("Loading Please Wait", { svgColor: "#E52324" });
    } else {
      Loading.remove();
    }
  }, [adminloading]);

  const filteredUsers = useMemo(() => {
    const list = Array.isArray(AdminUsers) ? AdminUsers : [];
    if (roleFilter === "All") return list;
    return list.filter((u) => (u?.role || "").toLowerCase() === roleFilter.toLowerCase());
  }, [AdminUsers, roleFilter]);

  const handleDeleteUser = (id) => {
    const MySwal = withReactContent(Swal);
    MySwal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
      confirmButtonColor: "#E52324",
      cancelButtonColor: "#1a2744",
      background: "#0B1120",
      color: "#fff",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(DeleteUserProfiel(id));
      }
    });
  };

  return (
    <div className="p-6 sm:p-8">
      <div className="mb-6">
        <h1 className="text-[22px] text-white" style={{ fontWeight: 800 }}>
          Customers
        </h1>
        <p className="text-white/30 text-[13px] mt-0.5">
          Total customers ({AdminUsers?.length ?? 0})
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        <Filter className="w-4 h-4 text-white/20 shrink-0" strokeWidth={1.8} />
        {ROLE_FILTERS.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setRoleFilter(r)}
            className={`px-3 py-1.5 rounded-lg text-[11px] transition-all ${
              roleFilter === r
                ? "bg-[#E52324]/15 text-[#E52324] border border-[#E52324]/20"
                : "text-white/30 hover:text-white/50 border border-transparent hover:bg-white/[0.03]"
            }`}
            style={{ fontWeight: 600 }}
          >
            {r === "All" ? "All" : formatRoleLabel(r)}
          </button>
        ))}
      </div>

      <div className="bg-[#0B1120] border border-[#1a1f30] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#1a1f30]">
                {[
                  "Id",
                  "Avatar / Name",
                  "Email",
                  "Mobile Number",
                  "Role",
                  "Created At",
                  "Action",
                ].map((h) => (
                  <th
                    key={h}
                    className="text-left px-5 py-3.5 text-[9px] text-white/20 uppercase tracking-[0.15em] whitespace-nowrap"
                    style={{ fontWeight: 700 }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length > 0 ? (
                filteredUsers.map((item, index) => {
                  const displayName = getDisplayName(item);
                  const avatarColor = getAvatarColor(displayName, index);
                  const initials =
                    (item?.firstName?.slice(0, 1) || item?.email?.slice(0, 1) || "U").toUpperCase();

                  return (
                    <tr
                      key={item?._id || index}
                      className="border-b border-[#1a1f30]/30 last:border-b-0 hover:bg-white/[0.015] transition-colors"
                    >
                      <td className="px-5 py-3.5 text-[11px] text-white/20">{index + 1}</td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2.5 min-w-[160px]">
                          {item?.Avatar ? (
                            <img
                              src={`${ApiMediaURL}/${item.Avatar}`}
                              alt=""
                              className="w-7 h-7 rounded-full object-cover shrink-0 border border-[#1a1f30]"
                            />
                          ) : (
                            <div
                              className="w-7 h-7 rounded-full flex items-center justify-center text-[9px] text-white shrink-0"
                              style={{ fontWeight: 700, background: avatarColor }}
                            >
                              {initials}
                            </div>
                          )}
                          <span className="text-[12px] text-white/80" style={{ fontWeight: 600 }}>
                            {displayName}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-[11px] text-white/30 max-w-[180px] truncate">
                        {item?.email || "—"}
                      </td>
                      <td className="px-5 py-3.5 text-[11px] text-white/30 whitespace-nowrap">
                        {item?.phoneNumber || "—"}
                      </td>
                      <td className="px-5 py-3.5">
                        <button
                          type="button"
                          onClick={() =>
                            dispatch(
                              UpdateUserRole(
                                item?._id,
                                item?.role === "user" ? "admin" : "user"
                              )
                            )
                          }
                          className={`inline-flex px-2 py-0.5 rounded-full text-[9px] uppercase tracking-wider cursor-pointer transition-opacity hover:opacity-80 ${getRoleBadgeClass(item?.role)}`}
                          style={{ fontWeight: 600 }}
                          title="Click to toggle role"
                        >
                          {formatRoleLabel(item?.role)}
                        </button>
                      </td>
                      <td className="px-5 py-3.5 text-[11px] text-white/25 whitespace-nowrap">
                        {item?.createdAt ? String(item.createdAt).slice(0, 10) : "—"}
                      </td>
                      <td className="px-5 py-3.5 relative">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleDeleteUser(item._id)}
                            className="text-[10px] text-red-400/80 hover:text-red-400 px-2.5 py-1.5 rounded-lg border border-red-500/20 hover:bg-red-500/10 transition-all"
                            style={{ fontWeight: 500 }}
                          >
                            Delete
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setOpenMenuIndex(openMenuIndex === index ? null : index)
                            }
                            className="text-[10px] text-white/30 hover:text-white px-2.5 py-1.5 rounded-lg border border-white/[0.06] hover:bg-white/[0.04] transition-all"
                            style={{ fontWeight: 500 }}
                          >
                            Options
                          </button>
                        </div>
                        {openMenuIndex === index && (
                          <>
                            <button
                              type="button"
                              className="fixed inset-0 z-10"
                              onClick={() => setOpenMenuIndex(null)}
                              aria-label="Close menu"
                            />
                            <div className="absolute right-5 top-full mt-1 z-20 min-w-[160px] py-1 rounded-xl bg-[#111827] border border-[#1a2744] shadow-xl">
                              <button
                                type="button"
                                className="w-full text-left px-4 py-2 text-[12px] text-white/70 hover:bg-white/5 hover:text-white"
                                onClick={() => {
                                  dispatch(
                                    UpdateUserRole(
                                      item?._id,
                                      item?.role === "user" ? "admin" : "user"
                                    )
                                  );
                                  setOpenMenuIndex(null);
                                }}
                              >
                                {item?.role === "user" ? "Make Admin" : "Make User"}
                              </button>
                              <button
                                type="button"
                                className="w-full text-left px-4 py-2 text-[12px] text-white/70 hover:bg-white/5 hover:text-white"
                                onClick={async () => {
                                  setActive(9);
                                  await dispatch(GetUserOrderById(item?._id));
                                  setOpenMenuIndex(null);
                                }}
                              >
                                Orders details
                              </button>
                            </div>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={7} className="px-5 py-16 text-center text-white/30 text-[13px]">
                    {roleFilter === "All"
                      ? "No customers found."
                      : `No customers with role "${formatRoleLabel(roleFilter)}".`}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CustomerTable;
